import {
  Controller,
  Input,
  Loader,
  MaterialIcon,
  Tooltip,
  useFormContext,
  z,
} from '@qred/components-library';
import { ComboboxData } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';
import { Select } from '@qred/components-library/next';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { logToSentry } from '~/helpers/loggers.helper';
import { requiredString } from '~/helpers/schema.helper';
import { houseNumberAdditionsDropdownValuesHelper } from '~/helpers/shared.helper';
import { digitValidator, zipCodeValidator } from '~/helpers/validators.helper';
import useTranslate from '~/hooks/useTranslate';
import { apiGetAddress } from '~/services/api';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';

const TOOLTIP_MAX_WIDTH = 500;

export const NLAddressLookupSchema = z.object({
  address: z.object({
    zipCode: requiredString({
      validator: (value) => !zipCodeValidator(value),
      message: 'ValidationErrors.Zip',
    }),
    houseNumber: requiredString({
      validator: (value) => !digitValidator(value),
      message: 'ValidationErrors.HouseNumber',
    }),
    houseNumberAddition: requiredString().optional(),
    streetName: requiredString(),
    city: requiredString(),
  }),
});

export type NLAddressLookupForm = z.infer<typeof NLAddressLookupSchema>;

export const NLAddressLookup = () => {
  const market = useSelector((state: RootState) => state.intl.market);
  const t = useTranslate();

  const dispatch = useDispatch();
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [
    houseNumberAdditions,
    setHouseNumberAdditions,
  ] = useState<ComboboxData>([]);

  const {
    control,
    setValue,
    watch,
    register,
    formState: { errors, dirtyFields },
    setError,
    clearErrors,
  } = useFormContext<NLAddressLookupForm>();
  const zipCode = watch('address.zipCode');
  const houseNumber = watch('address.houseNumber');
  const houseNumberAddition = watch('address.houseNumberAddition');
  const streetName = watch('address.streetName');
  const city = watch('address.city');

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setValue(name as FieldPath<NLAddressLookupForm>, trimmedValue);
    dispatch(
      pushToGtmLoanAction({
        actionName: `${name}_change`,
      })
    );
  };

  const fetchAddress = useCallback(
    async (_zipCode: string, _houseNumber: string) => {
      try {
        setIsAddressLoading(true);

        const addressData = await apiGetAddress(_zipCode, _houseNumber, market);

        if (addressData) {
          setValue('address.city', addressData.city);
          setValue('address.streetName', addressData.street);

          const houseNumberAdditionValues = houseNumberAdditionsDropdownValuesHelper(
            addressData.houseNumberAdditions
          );
          const _houseNumberAdditions: ComboboxData = houseNumberAdditionValues.map(
            (value) => {
              let label = value;
              if (label === 'NoAdditionalHouseNumber') {
                label = t('AddressFields.NoAdditionalHouseNumber') as string;
              } else if (label === 'Select') {
                label = t('AddressFields.SelectAHouseNumber') as string;
              }

              return { label, value, disabled: value === 'Select' };
            }
          );

          setHouseNumberAdditions(_houseNumberAdditions);
          if (_houseNumberAdditions.length) {
            if (
              !houseNumberAddition ||
              !houseNumberAdditionValues.includes(houseNumberAddition)
            ) {
              setValue('address.houseNumberAddition', '');
            }
          } else {
            setValue('address.houseNumberAddition', undefined);
          }
        }
        clearErrors('root.address');
      } catch (error) {
        setError('root.address', {
          type: 'manual',
          message: 'ValidationErrors.NoAddressFound',
        });
        logToSentry(error, 'NLAddressLookup fetchAddress');
      } finally {
        setIsAddressLoading(false);
      }
    },
    [houseNumberAddition]
  );

  const debouncedFetchAddress = useMemo(() => debounce(fetchAddress, 500), [
    fetchAddress,
  ]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetchAddress.cancel();
    };
  }, [debouncedFetchAddress]);

  // Trigger address fetch when NL address fields change
  useEffect(() => {
    const valid =
      zipCode &&
      houseNumber &&
      !zipCodeValidator(zipCode) &&
      !digitValidator(houseNumber);

    if (valid) {
      debouncedFetchAddress(zipCode, houseNumber);
    } else {
      debouncedFetchAddress.cancel();
    }
  }, [
    zipCode,
    houseNumber,
    debouncedFetchAddress,
    errors.address?.zipCode,
    errors.address?.houseNumber,
  ]);

  useEffect(() => {
    if (dirtyFields.address?.zipCode || dirtyFields.address?.houseNumber) {
      setValue('address.streetName', '');
      setValue('address.city', '');
      setHouseNumberAdditions([]);
      setValue('address.houseNumberAddition', undefined);
      clearErrors('root.address');
    }
  }, [zipCode, houseNumber]);

  return (
    <>
      <Input.Group orientation="horizontal">
        <Input
          {...register('address.zipCode', { onBlur })}
          label={t('AddressFields.PostCode')}
          rightSection={isAddressLoading ? <Loader /> : undefined}
          error={
            errors?.address?.zipCode?.message &&
            t(errors?.address?.zipCode?.message)
          }
          id="zipCode"
        />
        <Input
          {...register('address.houseNumber', { onBlur })}
          label={t('AddressFields.HouseNumber')}
          rightSection={
            isAddressLoading ? (
              <Loader />
            ) : (
              <Tooltip
                maxWidth={
                  document.body.clientWidth < TOOLTIP_MAX_WIDTH
                    ? `${document.body.clientWidth * 0.8}px`
                    : `${TOOLTIP_MAX_WIDTH}px`
                }
                label={t('AddressFields.TooltipHomeAddressExplanation')}
              >
                <MaterialIcon src="help" size={'24'} />
              </Tooltip>
            )
          }
          error={
            errors?.address?.houseNumber?.message &&
            t(errors?.address?.houseNumber?.message)
          }
          id="houseNumber"
        />
      </Input.Group>

      {streetName && city && (
        <>
          {Boolean(houseNumberAdditions.length) && (
            <Controller
              name="address.houseNumberAddition"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <Select
                    label={t('AddressFields.HouseNumberAddition')}
                    value={field.value}
                    data={houseNumberAdditions}
                    allowDeselect={false}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={
                      fieldState.error?.message && t(fieldState.error?.message)
                    }
                    id="houseNumberAddition"
                  />
                );
              }}
            />
          )}

          <Input
            {...register('address.streetName')}
            label={t('AddressFields.Street')}
            disabled
            id="streetName"
          />

          <Input
            {...register('address.city')}
            label={t('AddressFields.City')}
            id="city"
            disabled
          />
        </>
      )}
    </>
  );
};
